import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { ArrowRight, Check, ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { useCreateWorkspace } from '../../../entities/workspace/mutation/use-create-workspace';
import { useWorkspaceCreationStorageState } from '../hooks';

import { useUser } from '@/entities/auth/hooks';
import { getPageRoute } from '@/entities/auth/utils/get-login-redirect-path';
import { Button } from '@/shared/components/shadcn/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/shadcn/dropdown-menu';
import { Input } from '@/shared/components/shadcn/input';
import { cn } from '@/shared/utils/index';
import { showErrorDialog } from '@/shared/utils/show-error-dialog';

const workspaceNameSchema = z.object({
  workspaceName: z
    .string()
    .min(1, 'Workspace name is required')
    .max(20, 'Workspace names must be 20 characters or fewer')
    .regex(
      /^[a-zA-Z0-9가-힣ㄱ-ㅎㅏ-ㅣ\s\-_]+$/,
      'Only letters, numbers, spaces, hyphens and underscores allowed',
    ),
});

type WorkspaceNameFormData = z.infer<typeof workspaceNameSchema>;

const AVAILABLE_LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'ko', name: 'Korean' },
] as const;

export function WorkspaceNameStep() {
  const redirectPage = useSearch({
    from: '/workspace/create/name',
    select: (search) => search.redirectPage,
  });
  const navigate = useNavigate();
  const [workspaceData, setWorkspaceData] = useWorkspaceCreationStorageState();
  const [selectedLanguage, setSelectedLanguage] = useState(workspaceData.language || 'en');
  const { refetch: refetchUser } = useUser();
  const createWorkspaceMutation = useCreateWorkspace();

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<WorkspaceNameFormData>({
    resolver: zodResolver(workspaceNameSchema),
    mode: 'onChange',
    defaultValues: {
      workspaceName: workspaceData.workspaceName || '',
    },
  });

  const handleFormSubmit = async (data: WorkspaceNameFormData) => {
    if (!selectedLanguage) return;

    setWorkspaceData({
      workspaceName: data.workspaceName,
      language: selectedLanguage,
    });

    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const response = await createWorkspaceMutation.mutateAsync({
        workspaceName: data.workspaceName,
        language: selectedLanguage,
        timezone,
      });

      setWorkspaceData({
        workspaceName: '',
        language: '',
      });

      await refetchUser();

      navigate(getPageRoute(response.id, redirectPage));
    } catch (error) {
      await showErrorDialog({
        error,
        title: 'Failed to Create Workspace',
        message:
          "We couldn't create your workspace. Please try again or contact our support team if the problem persists.",
      });
    }
  };

  const selectedLanguageData = AVAILABLE_LANGUAGES.find(
    (language) => language.code === selectedLanguage,
  );

  return (
    <div className="bg-sidebar-background flex min-h-screen justify-center">
      <div className="mt-60 flex w-full max-w-152.5 flex-col gap-12 px-4">
        <header className="flex flex-row items-center gap-2 text-3xl font-bold">
          <span>👋</span>
          <h1>Welcome to Syncly creator</h1>
        </header>

        <form onSubmit={handleSubmit(handleFormSubmit)} className="flex w-full flex-col gap-8">
          <section className="flex flex-col gap-3">
            <h2 className="text-foreground text-xl leading-none font-medium tracking-tight">
              <label htmlFor="workspace-name">Name your workspace</label>
            </h2>
            <Input
              {...register('workspaceName')}
              id="workspace-name"
              aria-invalid={Boolean(errors.workspaceName)}
              aria-describedby={errors.workspaceName ? 'workspace-name-error' : undefined}
              placeholder="Workspace name"
              className="mt-2 h-10 text-base"
              disabled={createWorkspaceMutation.isPending}
            />
            {errors.workspaceName && (
              <p id="workspace-name-error" className="text-destructive text-sm">
                {errors.workspaceName.message}
              </p>
            )}
          </section>

          <section className="flex flex-col gap-3">
            <h2 className="text-foreground text-xl leading-none font-medium tracking-tight">
              Select language setting for Syncly AI
            </h2>
            <p className="text-muted-foreground text-sm leading-none font-medium tracking-tight">
              AI will deliver the output in the language of your choice.
            </p>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  className={cn(
                    'mt-2 h-10 w-32 justify-between text-base font-normal [&>span]:w-full',
                    !selectedLanguageData && 'text-muted-foreground',
                  )}
                  disabled={createWorkspaceMutation.isPending}
                >
                  <span>{selectedLanguageData?.name || 'Select language'}</span>
                  <ChevronDown data-icon="inline-end" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-32">
                <DropdownMenuGroup>
                  {AVAILABLE_LANGUAGES.map((language) => (
                    <DropdownMenuItem
                      key={language.code}
                      onClick={() => setSelectedLanguage(language.code)}
                      className="justify-between"
                    >
                      {language.name}
                      {selectedLanguage === language.code && <Check />}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuGroup>
              </DropdownMenuContent>
            </DropdownMenu>
          </section>

          <nav className="flex justify-end py-2">
            <Button
              type="submit"
              className="h-10 px-4 text-sm font-medium"
              loading={createWorkspaceMutation.isPending}
              disabled={!isValid || !selectedLanguage}
            >
              <span>All set</span>
              <ArrowRight data-icon="inline-end" />
            </Button>
          </nav>
        </form>
      </div>
    </div>
  );
}
